import type { RatingUnitId } from "./index";
import { ratingBounds } from "./registry";
import { notApplicableText } from "./scale-wording-packs";
import type { RespondentWording } from "./scale-wording-packs";
import { NOT_APPLICABLE_VALUE } from "./vocabularies";

/**
 * The respondent options of a rating scale: the one owner (C04-1). The
 * compiler writes them as a scale's choices and grid columns; the builder's
 * grid editor and the twin-test gate show the same list. Nothing else builds
 * a scale's point text or its N/A choice.
 */

/** A rating scale as a rating question or a matrix measure stores it. */
export interface StoredRatingScale {
  unit: RatingUnitId;
  anchors?: { minLabel?: string; midpointLabel?: string; maxLabel?: string };
  pointLabels?: unknown;
  includeNotApplicable?: boolean;
  notApplicableLabel?: unknown;
}

export interface RespondentScaleOption {
  value: number | string;
  text: string;
}

/** Why stored point labels were read differently from how they were written. */
export type RatingPointLabelIssue =
  | { kind: "binary_yes_no" }
  | { kind: "length_mismatch"; expected: number; received: number }
  | { kind: "all_blank" };

/**
 * A fully labelled scale as exactly one usable label per point, or undefined
 * when the scale reads as numbers. Malformed input degrades (a missing point
 * reads as its number) rather than dropping points; the draft validator is
 * the layer that surfaces these problems to the author, and `onIssue` lets the
 * compiler log them.
 */
export function respondentRatingPointLabels(
  scale: Pick<StoredRatingScale, "unit" | "pointLabels">,
  onIssue?: (issue: RatingPointLabelIssue) => void,
): Array<string> | undefined {
  const labels = scale.pointLabels;
  if (!Array.isArray(labels) || labels.length === 0) {
    return undefined;
  }
  if (scale.unit === "binary_yes_no") {
    onIssue?.({ kind: "binary_yes_no" });
    return undefined;
  }
  const [min, max] = ratingBounds(scale.unit);
  const span = max - min + 1;
  if (labels.length !== span) {
    onIssue?.({
      kind: "length_mismatch",
      expected: span,
      received: labels.length,
    });
  }
  const normalized: Array<string> = [];
  let labeledCount = 0;
  for (let index = 0; index < span; index += 1) {
    const raw = labels[index];
    const text = typeof raw === "string" ? raw.trim() : "";
    if (text.length > 0) {
      labeledCount += 1;
      normalized.push(text);
    } else {
      normalized.push(String(min + index));
    }
  }
  if (labeledCount === 0) {
    onIssue?.({ kind: "all_blank" });
    return undefined;
  }
  return normalized;
}

/**
 * One option per point of the scale's unit: the point's label when the scale
 * is fully labelled, otherwise its number, with the min/max anchor appended
 * to the end points ("1 - Not at all likely").
 */
export function respondentScalePoints(
  scale: Pick<StoredRatingScale, "unit" | "anchors" | "pointLabels">,
  onIssue?: (issue: RatingPointLabelIssue) => void,
): Array<RespondentScaleOption> {
  const pointLabels = respondentRatingPointLabels(scale, onIssue);
  const { anchors } = scale;
  const [min, max] = ratingBounds(scale.unit);
  const points: Array<RespondentScaleOption> = [];
  for (let value = min; value <= max; value += 1) {
    const label = pointLabels?.[value - min];
    if (label) {
      points.push({ value, text: label });
      continue;
    }
    const suffix =
      value === min && anchors?.minLabel
        ? ` - ${anchors.minLabel}`
        : value === max && anchors?.maxLabel
          ? ` - ${anchors.maxLabel}`
          : "";
    points.push({ value, text: `${value}${suffix}` });
  }
  return points;
}

/** The N/A choice; its text is the label or the study language's default. */
export function respondentNotApplicableOption(
  label: unknown,
  wording: Pick<RespondentWording, "notApplicableLabel">,
): RespondentScaleOption {
  return {
    value: NOT_APPLICABLE_VALUE,
    text: notApplicableText(label, wording),
  };
}

/**
 * Every option a respondent can pick on the scale: its points, then the N/A
 * choice when the scale declares one. `wording` is the study language's
 * respondent wording (`respondentWordingFor(draft.contentLanguage)`), never
 * the interface language.
 */
export function respondentScaleOptions(
  scale: StoredRatingScale,
  wording: Pick<RespondentWording, "notApplicableLabel">,
  onIssue?: (issue: RatingPointLabelIssue) => void,
): Array<RespondentScaleOption> {
  const points = respondentScalePoints(scale, onIssue);
  return scale.includeNotApplicable
    ? [
        ...points,
        respondentNotApplicableOption(scale.notApplicableLabel, wording),
      ]
    : points;
}

/**
 * Every option a respondent can pick on a rating question: a yes/no question
 * reads Yes then No in the study language, every other unit reads its
 * `respondentScaleOptions`; the N/A choice comes last when declared. The
 * compiler writes these as a single rating's choices, and questionnaire
 * coverage scores the same text.
 */
export function respondentRatingOptions(
  scale: StoredRatingScale,
  wording: Pick<RespondentWording, "notApplicableLabel" | "yes" | "no">,
  onIssue?: (issue: RatingPointLabelIssue) => void,
): Array<RespondentScaleOption> {
  if (scale.unit !== "binary_yes_no") {
    return respondentScaleOptions(scale, wording, onIssue);
  }
  const yesNo: Array<RespondentScaleOption> = [
    { value: 1, text: wording.yes },
    { value: 0, text: wording.no },
  ];
  return scale.includeNotApplicable
    ? [
        ...yesNo,
        respondentNotApplicableOption(scale.notApplicableLabel, wording),
      ]
    : yesNo;
}
